import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AptosNormal from "../../../../utils/Customfonts/Aptos.ttf";
import AptosBold from "../../../../utils/Customfonts/Aptos-Bold.ttf";
import AptosItalic from "../../../../utils/Customfonts/Aptos-Italic.ttf";
import {
  calculateContractSalary,
  calculateFulltimeSalary,
  getContractTableRows,
  getFulltimeTableRows,
  formatCurrency,
  convertNumberToWords,
} from "./offerJoiningUtils";

const DYC_LOGO = "/img/dycgpl-logo.webp";
const OFFER_FOOTER = "/img/offer_footer.png";
const CONTRACT_STAMP = "/img/contract_stamp.png";
const OFFER_STAMP = "/img/offer_stamp.png";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "Aptos",
  normalFont: "Aptos",
  italic: "italic",
  italicFont: "Aptos",
};

// --- DYNAMIC PDF CONFIGURATION ---
export const config = {
  margin: 36,
  borderMargin: 15,
  footerMargin: 20,

  headerimgHeight: 60,
  headerTextSize: 22,
  headerText: "DYC GLOBAL",
  headerLogo: "/img/dycgpl-logo.webp",
  stampimgHeight: 140,

  borderGap: 5,

  fontSize: 10.5,
  fontSizeSm: 9.5,
  fontSizeLg: 11.5,
  fontSizeTitle: 13,
  cellPadding: 3,
  sectionGap: 12,

  // Dynamic Page Boundaries
  get contentTop() {
    return this.headerimgHeight + this.borderGap + 25;
  },
  get contentBottom() {
    return this.footerMargin + 35;
  },
};

const getBaseStyles = () => ({
  fontSize: config.fontSize,
  cellPadding: config.cellPadding,
  lineColor: [0, 0, 0],
  lineWidth: 0.5,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "top",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
  fillColor: [240, 244, 247],
  valign: "middle",
});

const getTableMargins = () => ({
  left: config.margin,
  right: config.margin,
  top: config.contentTop,
  bottom: config.contentBottom,
});

const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,
  refCode: "",

  init(doc, refCode = "") {
    this.doc = doc;
    this.refCode = refCode;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;
    this.y = config.contentTop;
  },

  afterTable(gap) {
    this.y = this.doc.lastAutoTable.finalY + (gap ?? config.sectionGap);
  },

  advance(delta) {
    this.y += delta;
  },

  ensureSpace(needed = 30) {
    if (this.y + needed > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      this.y = config.contentTop;
    }
  },
};

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  try {
    const fontResponse = await fetch(fontUrl);
    const fontBuffer = await fontResponse.arrayBuffer();
    const fontBase64 = arrayBufferToBase64(fontBuffer);
    pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, fontBase64);
    pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
  } catch (err) {
    console.error("Font load error:", fontName, err);
  }
};

// 2-Pass Decorator
const addPageDecorators = (doc, totalPages, isJoining = false) => {
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);

    // Dynamic Header Logo
    if (config.headerimgHeight > 0) {
      try {
        const logoH = Math.round(config.headerimgHeight * 0.8);
        const logoW = logoH; // 1:1 square ratio
        const logoY = 8;
        const logoX = layout.pageWidth - config.margin - logoW;

        const textSize = config.headerTextSize || Math.round(config.headerimgHeight * 0.3);
        const textY = logoY + Math.round(logoH * 0.625);

        // Draw Configurable Header Text before Logo
        if (config.headerText) {
          doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
          doc.setFontSize(textSize);
          doc.setTextColor(15, 23, 42);
          doc.text(config.headerText, logoX - 10, textY, { align: "right" });
        }

        // Draw Configurable Logo Image
        const logoPath = config.headerLogo || DYC_LOGO;
        try {
          doc.addImage(logoPath, "WEBP", logoX, logoY, logoW, logoH);
        } catch (_) {
          try {
            doc.addImage(logoPath, "PNG", logoX, logoY, logoW, logoH);
          } catch (__) {
            doc.addImage(logoPath, logoX, logoY, logoW, logoH);
          }
        }
      } catch (err) {
        console.error("Header render error:", err);
      }
    }

    if (isJoining) continue;

    // Footer Rendering
    const baseFooterY = layout.pageHeight - config.footerMargin;

    if (page === 1) {
      const footerY = baseFooterY - 24;
      doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text("DYC GLOBAL PVT. LTD.", config.margin, footerY);

      doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
      doc.setFontSize(7.5);
      doc.setTextColor(90, 90, 90);
      doc.text("702, Lodha Supremus – District 2,", config.margin, footerY + 8);
      doc.text("Kolshet Thane (W) 400607 Maharashtra, India", config.margin, footerY + 15);
      doc.text("Telephone: +91 2249628259 | www.dycgpl.com", config.margin, footerY + 22);
    } else {
      const footerY = baseFooterY - 2;
      doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text("DYC GLOBAL PVT. LTD.", config.margin, footerY);
    }

    // Page Number bottom right
    const pad = (n) => String(n).padStart(2, "0");
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(8.5);
    doc.setTextColor(0);
    doc.text(
      `Page ${pad(page)} of ${pad(totalPages)}`,
      layout.pageWidth - config.margin - 55,
      baseFooterY - 2
    );
  }
};

const addParagraph = (text, isBold = false, gapAfter = 6, overrideFontSize = null) => {
  const fSize = overrideFontSize || config.fontSize;
  layout.ensureSpace(16);
  layout.doc.setFont(
    isBold ? customFontFamily.boldFont : customFontFamily.normalFont,
    isBold ? customFontFamily.bold : customFontFamily.normal
  );
  layout.doc.setFontSize(fSize);
  layout.doc.setTextColor(0);

  const lines = layout.doc.splitTextToSize(text, layout.contentWidth);
  const lineSpacing = fSize * 1.38;
  lines.forEach((line) => {
    layout.ensureSpace(lineSpacing);
    layout.doc.text(line, config.margin, layout.y);
    layout.advance(lineSpacing);
  });
  layout.advance(gapAfter);
};

// Signature Block
const addSignatureBlock = (record, stampImg) => {
  const stampH = config.stampimgHeight;
  const stampW = stampH * 1.25;

  layout.ensureSpace(stampH + 25);
  const startY = layout.y + 10;
  const colW = layout.contentWidth / 2;

  // Left side: Company Stamp Image
  try {
    layout.doc.addImage(
      stampImg,
      "PNG",
      config.margin - 5,
      startY,
      stampW,
      stampH
    );
  } catch (_) { }

  // Right side: Candidate Acceptance Block
  const rightX = config.margin + colW + 15;
  const lineW = layout.contentWidth - (colW + 15);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(11);
  layout.doc.text("Accepted & Confirmed Above", rightX, startY + 28);

  const sigY = startY + 58;
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(10.5);
  layout.doc.text("Signature:", rightX, sigY);

  const candSignImg = record.candidateSign || record.candidateSignature || record.applicantSignature;
  if (candSignImg) {
    try {
      layout.doc.addImage(candSignImg, "PNG", rightX + 65, sigY - 20, 90, 30);
    } catch (_) { }
  }
  layout.doc.setLineWidth(0.5);
  layout.doc.line(rightX + 60, sigY, rightX + lineW, sigY);

  const nameY = startY + 80;
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(10.5);
  layout.doc.text("Name:", rightX, nameY);
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.text(String(record.candidateName || "").toUpperCase(), rightX + 45, nameY);

  const dateY = startY + 102;
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(10.5);
  layout.doc.text("Date:", rightX, dateY);
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  const acceptanceDateStr =
    (record.docType === "appointment"
      ? record.apptAcceptanceDateFormatted
      : record.offerAcceptanceDateFormatted) ||
    record.offerAcceptanceDateFormatted ||
    record.joiningDateFormatted ||
    record.offerDateFormatted ||
    "";
  layout.doc.text(String(acceptanceDateStr), rightX + 45, dateY);

  layout.advance(stampH + 25);
};

// CONTRACT OFFER LETTER PDF BUILDER
const buildContractOfferPDF = (doc, record) => {
  const calc = calculateContractSalary(record);
  const tableRows = getContractTableRows(record);

  // PAGE 1
  layout.init(doc, record.offerRefCode);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeSm);
  layout.doc.text(record.offerRefCode, config.margin, layout.y);
  layout.doc.text(`Date: ${record.offerDateFormatted}`, layout.pageWidth - config.margin - 90, layout.y);
  layout.advance(26);

  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.setFontSize(config.fontSize);
  layout.doc.text("To,", config.margin, layout.y); layout.advance(13.5);
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeLg);
  layout.doc.text(`${record.candidateName},`, config.margin, layout.y); layout.advance(14.5);

  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.setFontSize(config.fontSize);
  if (record.candidateAddress) { layout.doc.text(`Address: ${record.candidateAddress}`, config.margin, layout.y); layout.advance(13.5); }
  if (record.candidateMobile) { layout.doc.text(`Mobile: ${record.candidateMobile}`, config.margin, layout.y); layout.advance(13.5); }
  if (record.candidateEmail) { layout.doc.text(`Email: ${record.candidateEmail}`, config.margin, layout.y); layout.advance(13.5); }
  layout.advance(6);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text(`Dear ${String(record.candidateName || "").split(" ")[0]},`, config.margin, layout.y); layout.advance(16);

  layout.doc.setFontSize(config.fontSizeLg);
  layout.doc.text("Sub: Contract Offer Letter with Contract Fees Break-up", config.margin, layout.y); layout.advance(18);

  addParagraph(`Referring to your application and the interview you attended with us, we are delighted to offer you the position of "${record.position || "Sr. Inspection Engineer"}" with DYC Global Private Limited, Thane.`);
  addParagraph("Please refer to the attached Contract Fees Break-up (CTC Annexure A) for details of your remuneration.");
  addParagraph(`You are required to report on your duties on or before ${record.joiningDateFormatted}, at 9:30 am and you shall be reporting to Mr. Parag Wadekar.`);

  layout.advance(2);
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.text(`Your place of reporting / work will be ${record.candidateLocation || "Dahej, Gujarat"}.`, config.margin, layout.y); layout.advance(13.5);
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text("TKIL Industries Pvt. Ltd. (formerly known as thyssenkrupp Industries India Pvt. Ltd.)\nDahej Shipyard, Jageshwar, Bharuch, Gujarat- 392130.", config.margin + 10, layout.y);
  layout.advance(26);

  addParagraph("However, you may be required to work anywhere in India, if the company so directs, based on the exigencies of work.");

  addParagraph("Please provide the following documents on or before the joining date:", true);
  const docs = [
    "• 2 Nos. Colour Passport Size Photograph with White Background.",
    "• Proof of Salary drawn from previous employer. (Last 3 months' salary slips if applicable).",
    "• Relieving Letters from last employer.",
    "• Experience certificates from last employer.",
    "• Self-attested PANCARD Copy.",
    "• Aadhar Card copy or any other permanent address Proof & contact details.",
    "• Copies of Credentials / Educational certificates / Testimonials.",
    "• Professional Certification / Courses (NDT Test Certificates).",
  ];
  docs.forEach((d) => addParagraph(d));

  // PAGE 2
  layout.doc.addPage();
  layout.y = config.contentTop;

  addParagraph("This contract offer will remain valid for 7 working days from the date of issuance. Please confirm your date of joining by signing and returning the attached Contract Fees Annexure A.");
  addParagraph("In case of any delay, the company reserves the right to withdraw or cancel this Contract Offer.");
  addParagraph("This contract offer is contingent upon successfully clearing the client interview (if required), receiving client approval, and completing a satisfactory reference check.");
  addParagraph("The detailed Service Contract Letter will be issued within 7 working days of your joining.");
  addParagraph("This contract will remain valid from your date of joining or until the completion of the client contract, whichever is earlier. Your services will be on a contractual basis, and the duration may be shortened, extended, or made transferable as per project or client requirements.");
  addParagraph("We look forward to a long-term and mutually beneficial association.");

  addSignatureBlock(record, CONTRACT_STAMP);

  // PAGE 3 (Annexure A Table + Notes 1 to 7)
  layout.doc.addPage();
  layout.y = config.contentTop;

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeSm);
  layout.doc.text(`Name: ${record.candidateName}`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Location: ${record.candidateLocation || "Dahej"}`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Designation: ${record.position || "Sr. Inspection Engineer"}`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Department: Inspection Services`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Grade: E3`, config.margin, layout.y); layout.advance(18);

  layout.doc.setFontSize(config.fontSizeTitle);
  layout.doc.text("CTC Annexure A – Contract Fees Break-up", layout.pageWidth / 2, layout.y, { align: "center" });
  layout.advance(16);

  const head = [["Monthly Allowances (A)", "Monthly (INR)", "Yearly (INR)"]];
  const body = tableRows.map((r) => [r.label, formatCurrency(r.monthly), formatCurrency(r.yearly)]);
  body.push([{ content: "Total (Monthly)", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } }, formatCurrency(calc.cTotalM), formatCurrency(calc.cTotalY)]);

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    head,
    body,
    theme: "grid",
    headStyles: getHeaderStyles(),
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: layout.contentWidth * 0.5 }, 1: { halign: "right" }, 2: { halign: "right" } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable(12);

  addParagraph("Notes:", true);
  const p3Notes = [
    "1. The disbursement of the above components would depend upon the Service Rules.",
    "2. Medical Insurance: Up to amount declared above or insurance cover will be provided as per company policies for the same.",
    "3. All payments made under this agreement shall be subject to applicable statutory deductions as per prevailing laws. A deduction of 1% towards Tax Deducted at Source (TDS) shall be applicable, in accordance with the relevant regulations.",
    "4. Leaves: Leave entitlement will be governed by the prevailing HR policy / Leave Card.",
    "5. Contract Duration: This contract is initially for a period of 3 months and may be extended for a further period of 6 months based on performance and project requirements.",
    "6. Accommodation & Local Travel: Accommodation and local travel will be provided by the client.",
    "7. Food: Food arrangements will be managed by you.",
  ];
  p3Notes.forEach((n) => addParagraph(n));

  // PAGE 4 (Note 8 + Signature Block)
  layout.doc.addPage();
  layout.y = config.contentTop;

  const p4Notes = [
    "8. Mobilization & Demobilization Travel: The Company shall reimburse travel expenses incurred for mobilization and demobilization, in accordance with its prevailing policies.\nFor mobilization (joining), the employee may opt to travel by either train three tier or economy-class airfare. For demobilization (return), travel shall be undertaken by three tier train. All reimbursements will be processed upon submission of valid supporting documents and in line with company reimbursement guidelines.",
  ];
  p4Notes.forEach((n) => addParagraph(n));

  addSignatureBlock(record, CONTRACT_STAMP);
};

// FULL-TIME OFFER LETTER PDF BUILDER
const buildFulltimeOfferPDF = (doc, record) => {
  const fullData = getFulltimeTableRows(record);
  const calc = fullData.totals;

  // PAGE 1
  layout.init(doc, record.offerRefCode);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeSm);
  layout.doc.text(record.offerRefCode, config.margin, layout.y);
  layout.doc.text(`Date: ${record.offerDateFormatted}`, layout.pageWidth - config.margin - 90, layout.y);
  layout.advance(26);

  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.setFontSize(config.fontSize);
  layout.doc.text("To,", config.margin, layout.y); layout.advance(13.5);
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeLg);
  layout.doc.text(`${record.candidateName},`, config.margin, layout.y); layout.advance(14.5);

  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.setFontSize(config.fontSize);
  if (record.candidateAddress) { layout.doc.text(`Address: ${record.candidateAddress}`, config.margin, layout.y); layout.advance(13.5); }
  if (record.candidateMobile) { layout.doc.text(`Mobile: ${record.candidateMobile}`, config.margin, layout.y); layout.advance(13.5); }
  if (record.candidateEmail) { layout.doc.text(`Email: ${record.candidateEmail}`, config.margin, layout.y); layout.advance(13.5); }
  layout.advance(6);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text(`Dear ${String(record.candidateName || "").split(" ")[0]},`, config.margin, layout.y); layout.advance(16);

  layout.doc.setFontSize(config.fontSizeLg);
  layout.doc.text("Sub: Offer Letter with Salary Break-up", config.margin, layout.y); layout.advance(18);

  addParagraph(`Referring to your application and the interview you attended with us, we are delighted to offer you the position of "${record.position || "Project Coordinator and Business Development Executive"}" with DYC Global Private Limited, Thane.`);
  addParagraph("Please refer to the attached Salary Break-up (CTC Annexure A) for details of your remuneration.");
  addParagraph(`You are required to report on your duties on or before ${record.joiningDateFormatted}, at 9:30 am and you shall be reporting to Ms. Komal Dushing- Line Manager & Mr. Parag Wadekar – MD Technical.`);

  layout.advance(2);
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.text("Your place of reporting / work will be our Thane office.", config.margin, layout.y); layout.advance(13.5);
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text("DYC Global Pvt. Ltd.\n702, Lodha Supremus,\nDistrict-II, Kolshet,\nThane West – 400607.", config.margin + 10, layout.y);
  layout.advance(48);

  addParagraph("However, you may be required to work anywhere in India, if the company so directs, based on the exigencies of work.");

  addParagraph("Please provide the following documents on or before the joining date:", true);
  const docs = [
    "• 2 Nos. Colour Passport Size Photograph with White Background.",
    "• Proof of Salary drawn from previous employer. (Last 3 months' salary slips if applicable).",
    "• Relieving Letters from last employer.",
    "• Experience certificates from last employer.",
    "• Self-attested PANCARD Copy.",
    "• Aadhar Card copy or any other permanent address Proof & contact details.",
    "• Copies of Credentials / Educational certificates / Testimonials.",
    "• Professional Certification / Courses (NDT Test Certificates).",
  ];
  docs.forEach((d) => addParagraph(d));

  // PAGE 2
  layout.doc.addPage();
  layout.y = config.contentTop;

  addParagraph("This offer will remain valid for 7 working days from the date of issuance. Please confirm your date of joining by signing and returning the attached Salary Break-up.");
  addParagraph("In case of any delay, the company reserves the right to withdraw or cancel this offer.");
  addParagraph("This offer is contingent upon successfully clearing the client interview (if required), receiving client approval, and completing a satisfactory reference check.");
  addParagraph("The detailed Appointment Letter will be issued within 7 working days of your joining.");
  addParagraph("We look forward to a long-term and mutually beneficial association.");

  addSignatureBlock(record, OFFER_STAMP);

  // PAGE 3 (Annexure A Table + Notes 1 to 4)
  layout.doc.addPage();
  layout.y = config.contentTop;

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeSm);
  layout.doc.text(`Name: ${record.candidateName}`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Location: ${record.candidateLocation || "Thane, Mumbai"}`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Designation: ${record.position}`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Department: Inspection / Business Development`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Grade: K1`, config.margin, layout.y); layout.advance(18);

  layout.doc.setFontSize(config.fontSizeTitle);
  layout.doc.text("CTC Annexure A - Salary Break-up", layout.pageWidth / 2, layout.y, { align: "center" });
  layout.advance(16);

  const head = [["Particulars", "Monthly", "Yearly"]];
  const body = fullData.subA.map((r) => [r.label, formatCurrency(r.monthly), formatCurrency(r.yearly)]);
  body.push([{ content: "Sub Total (A)", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } }, formatCurrency(calc.subAM), formatCurrency(calc.subAY)]);
  fullData.subB.forEach((r) => {
    body.push([r.label, r.monthlyLabel || (r.monthly ? formatCurrency(r.monthly) : ""), formatCurrency(r.yearly)]);
  });
  body.push([{ content: "Sub Total B", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } }, "", formatCurrency(calc.subBY)]);
  body.push([{ content: "Total (A+B)", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } }, "", formatCurrency(calc.totalY)]);

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    head,
    body,
    theme: "grid",
    headStyles: getHeaderStyles(),
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: layout.contentWidth * 0.5 }, 1: { halign: "right" }, 2: { halign: "right" } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable(12);

  addParagraph("Notes:", true);
  const p3Notes = [
    "1. This offer is subject to satisfactory reference checks conducted by the Company.",
    "2. Your primary place of work shall be Lodha Supremus, Thane (West). However, depending on business requirements, you may be required to work at any other location within India. You may also be required to provide services to the Company's subsidiaries, affiliates, or associates, as and when directed.",
    "3. The disbursement of salary and related components will be governed by the Company's Service Rules, as applicable and amended from time to time.",
    "4. Performance Incentive shall be governed by the overall performance of the Company, your individual performance, and the discretion of Management. The entitlement and qualifying criteria will be determined on a year-to-year basis in accordance with the Company's incentive scheme and rules.",
  ];
  p3Notes.forEach((n) => addParagraph(n));

  // PAGE 4 (Notes 5 to 15)
  layout.doc.addPage();
  layout.y = config.contentTop;

  const p4Notes = [
    "5. Provident Fund, Professional Tax, and Income Tax shall be deducted from your salary in accordance with statutory provisions, effective from your date of joining. Gratuity benefits shall also be applicable from your date of joining, in line with statutory requirements.",
    "6. Expenses relating to Books & Periodicals, Telephone/Mobile/Data Card, Lunch Subsidy, and Travelling may be claimed as reimbursements on submission of valid bills, subject to limits prescribed by the Company. Such reimbursements shall qualify for tax exemption in accordance with prevailing laws. Reimbursement of First-Class Train Travel expenses shall also be available on submission of bills, subject to applicable limits. These benefits will be effective from your date of joining.",
    "7. Group Health Insurance and Group Personal Accident Insurance coverage shall be provided as per Company policies, effective upon confirmation of your employment.",
    "8. Leave entitlements, including Casual Leave (CL), Privilege Leave (PL), and Sick Leave (SL), shall be applicable only after confirmation of your employment, in accordance with the Company's leave policy.",
    "9. You will initially be on probation for a period of six months (or such other period as may be communicated). Upon successful completion and subject to performance review, your services may be confirmed in writing.",
    "10. Your employment with the Company may be terminated by either party by giving 15 days' prior written notice or by making payment in lieu of such notice, in accordance with the Company's Service Rules and applicable laws. The Company reserves the right to relieve you from your duties with immediate effect upon payment of salary in lieu of the notice period.",
    "11. You are required to maintain the highest standards of integrity, confidentiality, and professional conduct during your employment. You shall not, without prior written approval, disclose any confidential or proprietary information of the Company to any third party.",
    "12. Your retirement shall be in accordance with the Company's policy and applicable statutory provisions.",
    "13. You are required to comply with all applicable laws, regulations, and Company policies, as amended from time to time.",
    "14. Upon your acceptance of this offer and completion of the necessary joining formalities, a formal Appointment Letter shall be issued to you.",
    "15. Normal working hours shall be 9:30 am to 6:00 pm from Monday to Saturday.",
  ];
  p4Notes.forEach((n) => addParagraph(n));

  // PAGE 5 (Notes 16 & 17 + Signature Block)
  layout.doc.addPage();
  layout.y = config.contentTop;

  const defaultNote16 = "As part of your responsibilities in Project Operations & Business Development Executive, you are expected to maintain the highest standards of professional decorum across all digital and social platforms utilized for official activities. Your LinkedIn and other professional profiles must remain updated to accurately reflect your role and association with the organization. All communications, representations, and interactions through these platforms should consistently align with the company's values, code of conduct, and brand image.";
  const defaultNote17 = "No allowance will be paid for daily office travel. For external client visits, travel will be reimbursed as per company policy within Mumbai. Payments will be made on the 15th of the subsequent month.";

  const note16Text = record?.note16 || record?.notes16 || defaultNote16;
  const note17Text = record?.note17 || record?.notes17 || defaultNote17;

  const p5Notes = [
    `16. ${note16Text}`,
    `17. ${note17Text}`,
  ];
  p5Notes.forEach((n) => addParagraph(n));

  addSignatureBlock(record, OFFER_STAMP);
};

// APPOINTMENT LETTER PDF BUILDER (EXACT 9 PAGES MATCHING OFFICIAL DYC APPOINTMENT LETTER)
const buildAppointmentPDF = (doc, record) => {
  const fullData = getFulltimeTableRows(record);
  const calc = fullData.totals;
  const ctcYearlyFormatted = formatCurrency(calc.totalY);
  const ctcWords = convertNumberToWords(calc.totalY);

  // PAGE 1
  layout.init(doc, record.apptRefCode || `DYC/HR-APPT/${String(record.id || 1).padStart(3, "0")}`);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeSm);
  layout.doc.text(record.apptRefCode || `DYC/HR-APPT/${String(record.id || 1).padStart(3, "0")}`, config.margin, layout.y);
  layout.doc.text(`Date: ${record.offerDateFormatted}`, layout.pageWidth - config.margin - 90, layout.y);
  layout.advance(18);

  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.setFontSize(config.fontSize);
  layout.doc.text("To,", config.margin, layout.y); layout.advance(13.5);
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeLg);
  layout.doc.text(`${record.candidateName},`, config.margin, layout.y); layout.advance(14.5);

  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.setFontSize(config.fontSize);
  if (record.candidateAddress) { layout.doc.text(`Address: ${record.candidateAddress}`, config.margin, layout.y); layout.advance(13.5); }
  if (record.candidateMobile) { layout.doc.text(`Mobile: ${record.candidateMobile}`, config.margin, layout.y); layout.advance(13.5); }
  if (record.candidateEmail) { layout.doc.text(`Email: ${record.candidateEmail}`, config.margin, layout.y); layout.advance(13.5); }
  layout.advance(6);

  layout.doc.setFontSize(config.fontSizeTitle);
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text("APPOINTMENT LETTER", layout.pageWidth / 2, layout.y, { align: "center" });
  layout.advance(18);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSize);
  layout.doc.text(`Dear ${String(record.candidateName || "").split(" ")[0]},`, config.margin, layout.y); layout.advance(16);

  addParagraph(`With reference to your application and subsequent interviews you had with us, we are delighted to appoint you as "${record.position || "Project Coordinator and Business Development Executive"}" in Grade K1 in our organization with effect from ${record.joiningDateFormatted}, subject to the following terms & conditions. This letter of appointment supersedes all other communications made to yours before the date of issue of this appointment letter.`);

  addParagraph("1. PLACE OF APPOINTMENT", true);
  addParagraph("Your place of work will be our office at 702, Lodha Supremus, District - II, Kolshet Road, Thane West - 400607. However, you may be required to work anywhere in India, if the company so directs, based on the exigencies of work. You may be posted or transferred to any of the Company's Establishments, Departments, Business Units, or Operations.");
  addParagraph("Your unwavering commitment to dedicating your undivided time and focus to the Company is of paramount importance. Accordingly, you are expressly prohibited from engaging directly or indirectly in any other business, occupation, or employment, whether part-time or full-time, that are either vendors or competitors of the Company.");
  addParagraph("In the interest of transparency and to maintain the integrity of our professional relationship, it is imperative that you disclose any potential conflicts of interest, including those involving your immediate family members, to the Company before formally accepting this appointment. This proactive disclosure is a crucial step in fostering transparency and maintaining the ethical standards expected in our professional collaboration.");

  addParagraph("2. REPORTING", true);
  addParagraph("You would be reporting to the Managing Director or to whosoever the company deems fit to be your Manager.");

  // PAGE 2 (COMPACT SPACING TO KEEP ALL POINTS A TO E TOGETHER ON PAGE 2)
  layout.doc.addPage();
  layout.y = config.contentTop;

  addParagraph("3. EMPLOYMENT TENURE", true, 4, 10);
  addParagraph("3.1 PROBATION", true, 3, 10);
  addParagraph("Upon commencement of your employment with the Company, you will undergo a probationary period lasting for Six Months. The management reserves the right to extend this period based on an evaluation of your conduct, regularity, and performance at work.", false, 4, 9.8);
  addParagraph("Your confirmation in the position will be communicated through a written confirmation letter. Until such confirmation is received, it shall be assumed that you remain under probation.", false, 4, 9.8);

  addParagraph("3.2 RESIGNATION DURING PROBATION PERIOD", true, 3, 10);
  addParagraph("In the event of your resignation during the probationary period, you will be relieved within 15 days from the date of submitting your resignation.", false, 4, 9.8);

  addParagraph("3.3 TERMINATION DURING PROBATIONARY PERIOD", true, 3, 10);
  addParagraph("During the probationary period, the Company retains the discretion to terminate your employment with immediate effect, without prior notice, and without assigning any specific reason.", false, 4, 9.8);

  addParagraph("3.4 CONFIRMATION", true, 3, 10);
  addParagraph("Your confirmation in the position is contingent upon your performance and overall conduct. It is imperative that this confirmation be conveyed in writing. In the absence of a written confirmation, it shall be understood that you remain in a probationary status.", false, 4, 9.8);

  addParagraph("3.5 RESIGNATION", true, 3, 10);
  addParagraph("In the event of your resignation, it is imperative to adhere to the Company's prescribed notice period, determined by your Grade at the time of resignation. This procedural requirement is fundamental to ensuring an organized and seamless transition, safeguarding the operational continuity of the Company. Your collaboration in meticulously following this protocol is valued for its contribution to the overall efficiency of the resignation process.", false, 4, 9.8);

  addParagraph("3.6 TERMINATION DUE TO MISCONDUCT", true, 3, 10);
  addParagraph("In the unfortunate event of your involvement in activities deemed as misconduct, as assessed by the Company, it grants the Company the authority to terminate your employment abruptly, without the obligation of providing prior notice or compensation in lieu of notice.", false, 3, 9.8);
  addParagraph("Additionally, your employment may face immediate termination without prior notice or compensation if, at any point:", false, 3, 9.8);
  addParagraph("a. You violate the terms outlined in this agreement.", false, 2, 9.8);
  addParagraph("b. You initiate or undergo bankruptcy proceedings.", false, 2, 9.8);
  addParagraph("c. You encounter a medical condition preventing you from fulfilling your job responsibilities.", false, 2, 9.8);
  addParagraph("d. A court finds you guilty of a severe offense involving moral turpitude and issues a corresponding sentence.", false, 2, 9.8);
  addParagraph("e. You engage in actions categorized as grave misconduct, insubordination, or indiscipline.", false, 2, 9.8);

  // PAGE 3
  layout.doc.addPage();
  layout.y = config.contentTop;

  addParagraph("3.7 RETIREMENT", true);
  addParagraph("The designated retirement age is set at 60 years. Upon reaching the age of 60, your retirement will be automatic, and no additional notice will be provided. This policy is aligned with standard retirement practices and ensures a seamless transition into the next phase.");

  addParagraph("3.8 SALARY AND BENEFITS DISCLOSURE", true);
  addParagraph(`Your total compensation CTC will be Rs ${ctcYearlyFormatted}/- (${ctcWords}), per annum which includes salary, leave salary, bonus, PF, gratuity, any other allowances such as conveyance, travelling, mobile expense and other benefits can be found in the attached CTC ANNEXURE A - Salary Break-up.`);
  addParagraph("It is imperative to note that no additional payments, remunerations, compensations, benefits, or perks, beyond what is explicitly outlined in this agreement or as per the Company's guidelines and policies, shall be extended.");
  addParagraph("The Annual Salary Review will be contingent upon the Company's overall performance, business outlook, and individual employee contributions throughout the appraisal year. It's essential to mention that employees under probationary status are not eligible for participation in the Annual Salary Review.");

  addParagraph("4. RESPONSIBILITIES", true);
  addParagraph("Detailed job responsibilities will be outlined in your job description, provided to you separately.");

  addParagraph("5. TRAINING AND PROFESSIONAL DEVELOPMENT", true);
  addParagraph("Throughout your tenure with the Company, there may be instances where your participation in training programs or higher education becomes necessary for skill enhancement, business exigencies, or the improvement of client services. In such cases, the Company will bear the associated expenses.");
  addParagraph("In reciprocation for the provided training or higher education, you will be required to sign an agreement outlining your commitment to serve the Company for a specified duration as outlined in the aforementioned agreement post the completion of the training or education. In the event of a breach of this agreement or discontinuation of your services to the Company for any reason, you will be obligated to reimburse the Company for the incurred training expenses. The specific amounts for such reimbursement will be outlined in the aforementioned agreement.");

  addParagraph("6. SERVICE RULES AND COMPLIANCE", true);
  addParagraph("This appointment is subject to adherence to the comprehensive set of rules, regulations, and policies outlined in the Company's Human Resources (HR) Manual (DYC/HR/01), which may undergo periodic amendments, modifications, or cancellations. The HR Manual encompasses a spectrum of guidelines that necessitate your thorough understanding and strict compliance.");

  // PAGE 4
  layout.doc.addPage();
  layout.y = config.contentTop;

  addParagraph("You are obligated to adhere to the service rules applicable based on your designated location, encompassing aspects such as leave entitlement, working hours, Employee Cadre, Statutory Provisions, and Company Policies. Compliance with these rules ensures alignment with the prevailing terms and conditions of employment.");
  addParagraph("Furthermore, you are expected to abide by any additional rules, regulations, orders, directives and instructions that the Company may introduce over time, governing various facets of employment including but not limited to working conditions, pay and allowance, and more.");
  addParagraph("For detailed information regarding your leave entitlement, kindly refer to the Leave Rules stipulated in the Company HR Manual. Your commitment to understanding and complying with these regulations is integral to a harmonious and productive professional relationship.");

  addParagraph("7. NO SIMULTANEOUS EMPLOYMENT", true);
  addParagraph("While employed with this company, you will not in any circumstances be permitted to work for any other firm or person, either whole-time or part-time, to own, or in any way be associated with any firm or person as advisor, director or partner, whether paid or not, for your services, without prior written permission of the company.");

  addParagraph("8. CONFIDENTIALITY AND NON-DISCLOSURE OBLIGATIONS", true);
  addParagraph("During your employment and even after it ends, you are bound to keep company information confidential. This includes any formulas, processes, ideas, customer details, and other sensitive information you come across during your work. The Company may request you to sign additional confidentiality or non-disclosure agreements, emphasizing the importance of safeguarding such information.");
  addParagraph("These obligations remain in force even after your employment concludes, only becoming obsolete for information that becomes public without any fault on your part.");

  addParagraph("9. INTELLECTUAL PROPERTY OWNERSHIP", true);
  addParagraph("Any work-related documents, like designs, drawings, photos, plans, computer programs, or records, created by you during your employment belong to the Company. You agree to give up any intellectual property rights to these documents and promise to sign any necessary papers to transfer these rights. When you leave the Company, you must return all such documents and records.");

  addParagraph("10. DATA PROTECTION COMPLIANCE", true);
  addParagraph("You must follow all the rules about keeping information safe that apply to the Company.");

  addParagraph("11. NON-SOLICITATION AGREEMENT", true);
  addParagraph("You agree not to join or be involved with any company that competes with DYC Global Private Limited for a period of two years after you leave the company. Additionally, you commit not to start your own business that rivals any of the activities of DYC Global Private Limited. During this period, you won't try to persuade other employees to leave their jobs, and you won't attempt to divert business from DYC Global Private Limited by contacting vendors or customers.");

  // PAGE 5
  layout.doc.addPage();
  layout.y = config.contentTop;

  addParagraph("12. CUSTODY AND CARE OF COMPANY ASSETS", true);
  addParagraph("As an employee, it is your responsibility to ensure the safekeeping of any company property entrusted to you. In the event of loss or damage during your possession, you are liable for reimbursement. Upon conclusion of your employment, for any reason, it is imperative to return all company assets in satisfactory condition.");

  addParagraph("13. CODE OF CONDUCT, UNDERTAKING AND CONFLICT OF INTEREST", true);
  addParagraph("13.1 CODE OF CONDUCT", true);
  addParagraph("You shall follow the rules outlined in our company's HR Manual (DYC/HR/01). This manual defines the expected conduct for all employees, ensuring a positive and respectful work environment.");

  addParagraph("13.2 UNDERTAKING", true);
  addParagraph("You confirm that there are no prior agreements or service conditions from your past employment that would hinder you from joining DYC Global Private Limited.");
  addParagraph("You assure the accuracy, correctness, and completeness of all details provided in your job application, including supporting documents.");
  addParagraph("You commit to providing proof of identity, address, date of birth, nomination forms for retirement schemes, as well as relevant certificates and testimonials to support your qualifications and experience.");
  addParagraph("It is your responsibility to promptly inform us of any changes to this information within one week of such changes occurring.");

  addParagraph("13.3 CONFLICT OF INTEREST", true);
  addParagraph("In recognition of the critical importance of maintaining transparent professional conduct, You solemnly undertake and guarantee the following as an integral part of your commitment to DYC Global Private Limited:");
  addParagraph("• You assure that you do not harbour any personal interests that could potentially create a conflict with the interests of the Company.");
  addParagraph("• You commit to seeking prior approval from DYC Global Private Limited before accepting any directorship or assuming any other position, including roles within non-governmental organizations.");
  addParagraph("• You acknowledge and accept the responsibility to indemnify and protect DYC Global Private Limited from any and all losses, damages, costs, expenses, or injuries it may suffer or incur due to any breach of the representations, undertakings, and warranties made by you.");
  addParagraph("This undertaking signifies my dedication to upholding the highest standards of professional integrity and ensuring the best interests of DYC Global Private Limited are safeguarded at all times.");

  // PAGE 6
  layout.doc.addPage();
  layout.y = config.contentTop;

  addParagraph("14. DISCIPLINE", true);
  addParagraph("a) You will be governed by the company's rules and regulations that may be promulgated from time to time.");
  addParagraph("b) You shall not refuse to take up any assignment that may be offered to you by the company.");
  addParagraph("c) You shall be expected to abide by the rules and regulations of the company, be courteous, honest and professional within the company or with its clients/customers, and maintain & represent the Company's high standards of professional Services at all times, whether in the Company or at its client's site(s).");
  addParagraph("d) You shall be responsible for all company properties and material that are in your possession, and all infrastructure like telephones, computers, projectors etc that have been provided to you to enable you in your work.");
  addParagraph("e) You shall not publicly or privately criticize, defame or misrepresent the Company and shall not, knowingly, commit any such actions which may result in the Company's image / business being adversely affected.");
  addParagraph("f) During the course of your employment with the Company, you will not solicit business of any nature, either directly or indirectly, for yourself, or for any other party, from the company's clients and/or customers.");
  addParagraph("g) Your appointment shall be subject to your abiding by the rules and regulations of the company that are in force from time to time and during the period of your employment with the company.");
  addParagraph("h) The company shall have right to add, amend and modify any or all of the above terms and conditions of service which shall be binding on you.");

  addParagraph("15. POSTAL ADDRESS", true);
  addParagraph("You will keep the company informed of the postal address along with contact number for communication, including the changes that may occur during your period of employment with the company.");

  addParagraph("16. WORKING HOURS", true);
  addParagraph("You are expected to work & complete your 8.30 hours daily, between 9:30 AM to 6:00 PM daily. The working hours are subject to change, depending on the requirements of the Company. There will be weekly holidays on every Sunday. However, if required, you need to work on Sunday too and extra compensation will be paid for the same.");

  addParagraph("17. OWNERSHIP OF INVENTIONS, IDEAS, ETC.", true);
  addParagraph("a) The company shall be the sole owner of all inventions, ideas, materials, systems, codes, etc. that may be devised / created by the Employee in the course of his/ her employment with the company.");

  // PAGE 7
  layout.doc.addPage();
  layout.y = config.contentTop;

  addParagraph("b) The employee shall have no claim of any nature whatsoever and undertakes not to utilize, part with or divulge such information to any third party, even after he / she has ceased to be an employee of the company.");
  addParagraph("c) The above terms and conditions shall be binding upon both the parties hereto during the entire course of employment of the Employee with the company.");
  addParagraph("d) Apart from the above terms and conditions, you shall be bound by, and shall adhere to such other rules and regulations as may be in force, from time to time, in the Company.");

  addParagraph("18. AUTHENTICITY OF INFORMATION", true);
  addParagraph("This appointment is made on the basis of information provided by you in the application and also at the time of interview. Original copies of your certificates, testimonials and previous employment records should be submitted along with the photocopies for verification. If any declarations given by you to the Company prove to be false or if you are found to have wilfully suppressed any material information, you will be liable to be removed from service without any notice.");

  addParagraph("19. STATUTORY BENEFITS AND DEDUCTIONS", true);
  addParagraph("All payments made under this agreement are subject to deductions for taxes as per the prevailing rates applicable at the relevant time.");
  addParagraph("This appointment letter is also governed by the various prevailing rules, regulations and the HR Manual (DYC/HR/01) of the Company as amended, modified or cancelled from time to time.");
  addParagraph("The Company's HR Manual contains various rules and regulations which you are required to familiarize and ensure compliance. You shall be governed by service rules applicable as per location of posting including but not limited to, leave entitlement, working hours, Employee Cadre, Statutory Provisions and/or Company Policies in force from time to time as the case may be. You will also abide by such rules, regulations, orders, directives and instructions as the Company may frame from time to time governing the terms of employment, conditions of service, working conditions, pay and allowance, Quality Health Safety & Environment (QHSE) etc.");
  addParagraph("Your leave entitlement will be governed by the prevailing Leave Rules. The details are available in the Company HR Manual (DYC/HR/01).");

  addParagraph("20. JURISDICTION CLAUSES", true);
  addParagraph("In matters concerning this appointment, the governing authority is the Head Office of DYC Global Private Limited, situated in Thane Maharashtra.");
  addParagraph("In the event of any dispute or differences related to this contract or its termination, jurisdiction will lie with the courts and relevant authorities in Thane Maharashtra.");
  addParagraph("Your confirmation and acceptance of the outlined terms and conditions are kindly requested by signing and returning the enclosed duplicate copy of this appointment letter.");

  // PAGE 8
  layout.doc.addPage();
  layout.y = config.contentTop;

  addParagraph("We wish you every success in your career with us.");
  layout.advance(15);

  // Page 8 Sign-off & Acknowledgement block
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.text("Yours faithfully,", config.margin, layout.y); layout.advance(16);

  try {
    const stampH = config.stampimgHeight;
    const stampW = stampH * 1.25;
    layout.doc.addImage(CONTRACT_STAMP, "PNG", config.margin - 5, layout.y, stampW, stampH);
    layout.advance(stampH + 35); // Generous gap between stamp image and Acknowledgement
  } catch (_) {
    layout.advance(60);
  }

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeTitle);
  layout.doc.text("Acknowledgement", config.margin, layout.y); layout.advance(18);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSize);
  layout.doc.text("The above terms and conditions are acceptable to me", config.margin, layout.y); layout.advance(26);

  layout.doc.text("Signature: __________________________", config.margin, layout.y); layout.advance(22);
  layout.doc.text("Name: ", config.margin, layout.y);
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text(String(record.candidateName || "").toUpperCase(), config.margin + 45, layout.y); layout.advance(22);
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  const apptAcceptDate = record.apptAcceptanceDateFormatted || "";
  layout.doc.text(`Date: ${apptAcceptDate ? apptAcceptDate : "__________________________"}`, config.margin, layout.y);

  // PAGE 9 (CTC ANNEXURE A)
  layout.doc.addPage();
  layout.y = config.contentTop;

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeSm);
  layout.doc.text(`Name: ${record.candidateName}`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Location: ${record.candidateLocation || "Thane, Mumbai"}`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Designation: ${record.position}`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Department: Inspection / Business Development`, config.margin, layout.y); layout.advance(12);
  layout.doc.text(`Grade: K1`, config.margin, layout.y); layout.advance(18);

  layout.doc.setFontSize(config.fontSizeTitle);
  layout.doc.text("CTC Annexure - Salary Break-up", layout.pageWidth / 2, layout.y, { align: "center" });
  layout.advance(16);

  const head = [["Particulars", "Monthly", "Yearly"]];
  const body = fullData.subA.map((r) => [r.label, formatCurrency(r.monthly), formatCurrency(r.yearly)]);
  body.push([{ content: "Sub Total (A)", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } }, formatCurrency(calc.subAM), formatCurrency(calc.subAY)]);
  fullData.subB.forEach((r) => {
    body.push([r.label, r.monthlyLabel || (r.monthly ? formatCurrency(r.monthly) : ""), formatCurrency(r.yearly)]);
  });
  body.push([{ content: "Sub Total B", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } }, "", formatCurrency(calc.subBY)]);
  body.push([{ content: "Total (A+B)", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold } }, "", formatCurrency(calc.totalY)]);

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    head,
    body,
    theme: "grid",
    headStyles: getHeaderStyles(),
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: layout.contentWidth * 0.5 }, 1: { halign: "right" }, 2: { halign: "right" } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });
  layout.afterTable(12);

  addParagraph(`Remark : Notes mentioned in offer No. ${record.offerRefCode || "DYC/HR-OFFER-PR/001"} shall be applicable.`, true);
  layout.advance(10);
  addSignatureBlock(record, CONTRACT_STAMP);
};

// JOINING LETTER PDF BUILDER (EXACT DYC JOINING KIT STRUCTURE)
const buildJoiningPDF = (doc, record) => {
  layout.init(doc, record.jkRefCode);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeSm);
  layout.doc.text(record.jkRefCode, config.margin, layout.y);
  layout.doc.text(`Date: ${record.offerDateFormatted}`, layout.pageWidth - config.margin - 90, layout.y);
  layout.advance(20);

  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.setFontSize(config.fontSize);
  layout.doc.text("To,", config.margin, layout.y); layout.advance(14);
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSizeLg);
  layout.doc.text(`${record.candidateName},`, config.margin, layout.y); layout.advance(15);

  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.setFontSize(config.fontSize);
  if (record.candidateAddress) { layout.doc.text(`Address: ${record.candidateAddress}`, config.margin, layout.y); layout.advance(14); }
  if (record.candidateMobile) { layout.doc.text(`Mobile: ${record.candidateMobile}`, config.margin, layout.y); layout.advance(14); }
  if (record.candidateEmail) { layout.doc.text(`Email: ${record.candidateEmail}`, config.margin, layout.y); layout.advance(14); }
  layout.advance(8);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text(`Sub: Joining KIT`, config.margin, layout.y); layout.advance(18);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text(`Dear ${String(record.candidateName || "").split(" ")[0]},`, config.margin, layout.y); layout.advance(16);

  // Opening paragraph with "Joining Kit." in bold
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.text("We are pleased to present you with your ", config.margin, layout.y);
  const p1Width = layout.doc.getTextWidth("We are pleased to present you with your ");
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text("Joining Kit.", config.margin + p1Width, layout.y);
  const p2Width = layout.doc.getTextWidth("Joining Kit.");
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.text(" Please find enclosed the following", config.margin + p1Width + p2Width, layout.y);
  layout.advance(14);
  layout.doc.text("documents for your reference:", config.margin, layout.y);
  layout.advance(18);

  // The 6 Indented Items
  const docTitle = record.isContract ? "Service Contract Letter" : "Appointment Letter";
  const items = [
    { num: "1. ", bold: docTitle, rest: " – Kindly sign and return the duplicate copy to us." },
    { num: "2. ", bold: "Leave Card", rest: " – To be submitted to the Admin Department." },
    { num: "3. ", bold: "ID Card", rest: " – Please fill the attached Form and Submit." },
    { num: "4. ", bold: "Income Tax Declaration Form.", rest: "" },
    { num: "5. ", bold: "Independence, Impartiality, Integrity, and Confidentiality Declaration", rest: " (DYC/FMT-MR/13)." },
    { num: "6. ", bold: "Nomination Form -", rest: " Please fill the attached Form and Submit." },
  ];

  const indentX = config.margin + 20;
  items.forEach((item) => {
    layout.ensureSpace(16);
    layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    layout.doc.setFontSize(config.fontSize);
    layout.doc.text(item.num, indentX, layout.y);

    const numW = layout.doc.getTextWidth(item.num);
    layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    layout.doc.text(item.bold, indentX + numW, layout.y);

    if (item.rest) {
      const boldW = layout.doc.getTextWidth(item.bold);
      layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
      layout.doc.text(item.rest, indentX + numW + boldW, layout.y);
    }
    layout.advance(16);
  });
  layout.advance(10);

  // Induction paragraph with bold keywords
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.text("You are requested to complete ", config.margin, layout.y);
  const ind1W = layout.doc.getTextWidth("You are requested to complete ");
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text("Induction", config.margin + ind1W, layout.y);
  const ind2W = layout.doc.getTextWidth("Induction");
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.text(" module within ", config.margin + ind1W + ind2W, layout.y);
  const ind3W = layout.doc.getTextWidth(" module within ");
  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text("15 days", config.margin + ind1W + ind2W + ind3W, layout.y);
  const ind4W = layout.doc.getTextWidth("15 days");
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.text(" of your joining date.", config.margin + ind1W + ind2W + ind3W + ind4W, layout.y);
  layout.advance(18);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text("Link for the Online Induction module: ", config.margin, layout.y);
  const linkTitleW = layout.doc.getTextWidth("Link for the Online Induction module: ");
  layout.doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
  layout.doc.text("https://dycgpl.com/induction", config.margin + linkTitleW, layout.y);
  layout.advance(18);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.text("For any support or assistance, please feel free to contact us:", config.margin, layout.y);
  layout.advance(14);

  const head = [["Department", "Email ID", "Contact Number"]];
  const body = [
    ["Human Resource (HR)", "hr@dycgpl.com", "8652663355"],
    ["Information Technology (IT)", "itsupport@dycgpl.com", "9820991541"],
  ];

  autoTable(layout.doc, {
    startY: layout.y,
    margin: getTableMargins(),
    head,
    body,
    theme: "grid",
    headStyles: getHeaderStyles(),
    styles: getBaseStyles(),
    columnStyles: { 0: { cellWidth: layout.contentWidth * 0.33 }, 1: { cellWidth: layout.contentWidth * 0.34 }, 2: { cellWidth: layout.contentWidth * 0.33 } },
    didDrawPage: () => { layout.y = config.contentTop; },
  });

  // Generous gap below table for sign-off footer
  layout.afterTable(35);

  layout.doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
  layout.doc.setFontSize(config.fontSize);
  layout.doc.text("For DYC Global Pvt. Ltd.", config.margin, layout.y);
  layout.advance(45);
  layout.doc.text("HR Department.", config.margin, layout.y);
};

// MAIN EXPORT GENERATOR
export async function generateOfferJoiningPDF(record, setDownloadPdfloading) {
  try {
    if (setDownloadPdfloading) setDownloadPdfloading(true);

    const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4", compress: true });

    await Promise.all([
      loadAndAddFont(doc, AptosNormal, "Aptos", customFontFamily.normalFont, customFontFamily.normal),
      loadAndAddFont(doc, AptosBold, "Aptos-Bold", customFontFamily.boldFont, customFontFamily.bold),
      loadAndAddFont(doc, AptosItalic, "Aptos-Italic", customFontFamily.italicFont, customFontFamily.italic),
      loadAndAddFont(doc, AptosBold, "Aptos-BoldItalic", customFontFamily.normalFont, "bolditalic"),
    ]);

    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

    const isJoining = record.docType === "joining";
    const isAppointment = record.docType === "appointment";

    if (isJoining) {
      buildJoiningPDF(doc, record);
    } else if (isAppointment && !record.isContract) {
      buildAppointmentPDF(doc, record);
    } else if (record.isContract) {
      buildContractOfferPDF(doc, record);
    } else {
      buildFulltimeOfferPDF(doc, record);
    }

    addPageDecorators(doc, doc.getNumberOfPages(), isJoining);

    const candidateNameStr = String(record.candidateName || "Candidate").replace(/[^a-zA-Z0-9]/g, "_");
    const prefix = isJoining
      ? record.isContract ? "Joining_Contract_Letter" : "Joining_Payroll_Letter"
      : isAppointment
        ? "Appointment_Letter"
        : record.isContract ? "Contract_Letter" : "Offer_Letter";

    const fileName = `${prefix}_${candidateNameStr}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error("PDF generation error:", error);
  } finally {
    if (setDownloadPdfloading) setDownloadPdfloading(false);
  }
}
